import { Badge } from '@/components/ui/shadcn/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/shadcn/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/shadcn/dropdown-menu';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import { settingLabel } from '@/lib/status-labels';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';

interface InlineTypeCellProps {
    value: string | null | undefined;
    onUpdate?: (value: string) => void;
    className?: string;
    types?: string[];
    readOnly?: boolean;
}

export function InlineTypeCell({ value, onUpdate, className, types = [], readOnly = false }: InlineTypeCellProps) {
    const { t } = useTranslations();
    const isMobile = useIsMobile();
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleSelect = async (newValue: string) => {
        if (newValue === value) {
            setIsOpen(false);
            return;
        }

        if (!onUpdate) return;

        setIsLoading(true);
        try {
            await onUpdate(newValue);
            setIsOpen(false);
        } catch (error) {
            console.error('Failed to update type:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Types are stored as the raw display string the admin typed (e.g. "Client");
    // settingLabel resolves that through settings.company_types.* so the cell and
    // its dropdown follow the UI language.
    const options = types.map((type) => ({ value: type, label: settingLabel(t, 'company_types', type) }));
    const hasOptions = options.length > 0;
    const displayLabel = value ? settingLabel(t, 'company_types', value) : '—';

    // Read-only mode: just display the badge
    if (readOnly || !onUpdate || !hasOptions) {
        return (
            <div className={cn('flex w-full items-center justify-start px-2 py-1.5', className)}>
                <Badge variant="outline" className="text-left">
                    {displayLabel}
                </Badge>
            </div>
        );
    }

    if (isMobile) {
        return (
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogTrigger asChild>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsOpen(true);
                        }}
                        className={cn('hover:bg-accent w-full rounded px-2 py-1.5 transition-colors', 'flex items-center justify-start', className)}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                        ) : (
                            <Badge variant="outline" className="text-left">
                                {displayLabel}
                            </Badge>
                        )}
                    </button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md" onClick={(e) => e.stopPropagation()}>
                    <DialogHeader>
                        <DialogTitle>{t('companies.index.columns.type', 'Type')}</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col gap-2">
                        {options.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => handleSelect(option.value)}
                                className="bg-background hover:bg-accent flex w-full items-center justify-start rounded-md border px-3 py-2 text-left"
                            >
                                <Badge variant="outline">{option.label}</Badge>
                            </button>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsOpen(true);
                    }}
                    className={cn('hover:bg-accent w-full rounded px-2 py-1.5 transition-colors', 'flex items-center justify-start', className)}
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                    ) : (
                        <Badge variant="outline" className="text-left">
                            {displayLabel}
                        </Badge>
                    )}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" onClick={(e) => e.stopPropagation()}>
                {options.map((option) => (
                    <DropdownMenuItem key={option.value} onClick={() => handleSelect(option.value)} className="cursor-pointer">
                        <Badge variant="outline">{option.label}</Badge>
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
